import React, { useState } from "react";
import { Calendar, Badge, Tooltip, Modal, Button, Spin, message } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  useSchedulesForDateRange,
  useDeleteSchedule,
  Schedule,
  useStaff,
} from "../../hooks/useSchedule";
import { PlusOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import type { SelectInfo } from "antd/es/calendar/generateCalendar";
import ScheduleCalendarFilter, {
  CalendarFilterValues,
} from "./ScheduleCalendarFilter";
import ScheduleFormModal from "./ScheduleFormModal";
import ScheduleViewModal from "./ScheduleViewModal";
import {
  filterSchedules,
  groupSchedulesByDate,
  getBadgeStatus,
  capitalize,
} from "../../helpers/schedule";
import { useLocations } from "../../hooks/useUsers";
import { useTables } from "../../hooks/useTables";
import { PERMISSIONS, usePermissions } from "../../context/PermissionContext";

interface ScheduleCalendarProps {
  locationId?: number;
  canCreateSchedules: boolean;
  canModifySchedules: boolean;
  canDeleteSchedules: boolean;
}

// Helper function to get current user from localStorage
const getCurrentUser = () => {
  try {
    const userData =
      localStorage.getItem("user") || localStorage.getItem("currentUser");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error getting current user from localStorage:", error);
    return null;
  }
};

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  locationId,
  canCreateSchedules,
  canModifySchedules,
  canDeleteSchedules,
}) => {
  // Permission checks
  const { hasPermission } = usePermissions();
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);
  const canSeeAllSchedules = hasPermission(PERMISSIONS.CAN_SEE_ALL_SCHEDULES);

  const currentUser = getCurrentUser();

  // State definitions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<Schedule | null>(null);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  // Track current month being viewed
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());

  // Filters state with proper initialization (no automatic user filtering)
  const [filters, setFilters] = useState<CalendarFilterValues>({
    location_id: canSeeAllLocations ? locationId : currentUser?.location_id,
  });

  // Calculate date range for current month
  const startDate = currentMonth.startOf("month").format("YYYY-MM-DD");
  const endDate = currentMonth.endOf("month").format("YYYY-MM-DD");

  // API hooks - Convert UI filters to API filters (following UserManagementPage pattern)
  const apiFilters = React.useMemo(() => {
    const processedFilters = { ...filters };

    // Remove user_ids from API call since API doesn't support it
    if ("user_ids" in processedFilters) {
      delete processedFilters.user_ids;
    }

    // Remove date_range from API call since API expects separate start_date/end_date parameters
    if ("date_range" in processedFilters) {
      delete processedFilters.date_range;
    }

    // Handle user filtering logic
    if (!canSeeAllSchedules && currentUser?.id) {
      // If user can't see all schedules, always use their own ID regardless of selection
      processedFilters.user_id = currentUser.id;
    } else if (filters.user_ids && filters.user_ids.length > 0) {
      // If user can see all schedules and has selected someone, use the first selected user
      processedFilters.user_id = filters.user_ids[0];
    }

    return processedFilters;
  }, [filters, canSeeAllSchedules, currentUser?.id]);

  const { data: schedulesData, isLoading: isLoadingSchedules } =
    useSchedulesForDateRange(startDate, endDate, apiFilters);
  const deleteScheduleMutation = useDeleteSchedule();
  const { data: tablesData } = useTables();

  // Get locations and staff
  const { data: locationsData, isLoading: isLoadingLocations } = useLocations();
  const { data: staffData, isLoading: isLoadingStaff } = useStaff(
    canSeeAllLocations ? undefined : currentUser?.location_id
  );

  // Apply filters to schedules
  const filteredSchedules = React.useMemo(() => {
    const schedules = schedulesData?.data || [];
    return filterSchedules(schedules, filters);
  }, [schedulesData, filters]);

  // Group schedules by date for calendar display
  const schedulesByDate = React.useMemo(() => {
    return groupSchedulesByDate(filteredSchedules);
  }, [filteredSchedules]);

  // Find table number from table ID
  const getTableNumber = (tableId: number | null) => {
    if (!tableId || !tablesData?.data) return null;

    const table = tablesData.data.find((t) => t.id === tableId);
    return table ? table.number : null;
  };

  // Calendar cell renderer
  const dateCellRender = (value: Dayjs) => {
    const dateStr = value.format("YYYY-MM-DD");
    const schedules = schedulesByDate[dateStr] || [];

    return (
      <ul className="events">
        {schedules.map((schedule) => {
          const tableNumber = getTableNumber(schedule.table_id);

          return (
            <li key={schedule.id}>
              <Tooltip
                title={`${schedule.user.name} - ${schedule.shift_type} shift${
                  schedule.table_id
                    ? tableNumber
                      ? ` - Table #${tableNumber}`
                      : ` - Table (Loading...)`
                    : ""
                }`}
              >
                <Badge
                  status={getBadgeStatus(schedule.status)}
                  text={`${schedule.user.name.split(" ")[0]} (${capitalize(
                    schedule.shift_type
                  )})`}
                  className="cursor-pointer"
                  onClick={() => handleViewSchedule(schedule)}
                />
              </Tooltip>
            </li>
          );
        })}
      </ul>
    );
  };

  // Permission-aware handlers
  const handleDateSelect = (date: Dayjs, selectInfo: SelectInfo) => {
    setSelectedDate(date);
  };

  const handleCreateSchedule = () => {
    if (!canCreateSchedules) {
      message.error("You don't have permission to create schedules");
      return;
    }
    setCurrentSchedule(null);
    setIsFormModalVisible(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    if (!canModifySchedules) {
      message.error("You don't have permission to edit schedules");
      return;
    }
    setCurrentSchedule(schedule);
    setIsViewModalVisible(false);
    setIsFormModalVisible(true);
  };

  const handleViewSchedule = (schedule: Schedule) => {
    setCurrentSchedule(schedule);
    setIsViewModalVisible(true);
  };

  const handleDeleteSchedule = (id: number) => {
    if (!canDeleteSchedules) {
      message.error("You don't have permission to delete schedules");
      return;
    }
    Modal.confirm({
      title: "Delete Schedule",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to delete this schedule? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteScheduleMutation.mutateAsync(id);
          setIsViewModalVisible(false);
        } catch (error) {
          console.error("Error deleting schedule:", error);
        }
      },
    });
  };

  const handleFilter = (values: CalendarFilterValues) => {
    setFilters(values);
    setIsFilterVisible(false);
  };

  const handleResetFilter = () => {
    setFilters({
      location_id: canSeeAllLocations ? locationId : currentUser?.location_id,
    });
    setIsFilterVisible(false);
  };

  // Show total schedules count for debugging
  React.useEffect(() => {
    if (schedulesData) {
      console.log(
        `Calendar: Loaded ${schedulesData.total} schedules for ${startDate} to ${endDate}, filtered to ${filteredSchedules.length}`,
        { canSeeAllSchedules, currentUser: currentUser?.id, apiFilters }
      );
    }
  }, [
    schedulesData,
    filteredSchedules,
    startDate,
    endDate,
    canSeeAllSchedules,
    currentUser,
    apiFilters,
  ]);

  // Prepare locations and staff data for filters
  const locations = canSeeAllLocations ? locationsData || [] : [];
  const staff = staffData?.data || [];

  if (isLoadingSchedules || isLoadingLocations || isLoadingStaff) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <Spin size="large" />
        <div className="mt-4 text-gray-600">
          {isLoadingSchedules &&
            `Loading schedules for ${currentMonth.format("MMMM YYYY")}...`}
          {isLoadingLocations && "Loading locations..."}
          {isLoadingStaff && "Loading staff..."}
        </div>
      </div>
    );
  }

  const handleModalSubmit = () => {
    // Refresh schedules after creation or update
  };

  return (
    <div className="schedule-calendar">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {canSeeAllSchedules
            ? "Staff Schedule Calendar"
            : "My Schedule Calendar"}{" "}
          - {currentMonth.format("MMMM YYYY")}
        </h2>
        <div className="flex space-x-2">
          <Button onClick={() => setIsFilterVisible(!isFilterVisible)}>
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>
          {canCreateSchedules && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateSchedule}
            >
              Add Schedule
            </Button>
          )}
        </div>
      </div>

      {/* Filters - Calendar specific without date range */}
      {isFilterVisible && (
        <ScheduleCalendarFilter
          initialValues={filters}
          onFilter={handleFilter}
          onReset={handleResetFilter}
          locations={locations}
          staff={staff}
          canSeeAllSchedules={canSeeAllSchedules}
        />
      )}

      <Calendar
        cellRender={dateCellRender}
        onSelect={handleDateSelect}
        onPanelChange={(date) => {
          // Update current month when user navigates to different month
          setCurrentMonth(date);
        }}
      />

      {/* Create/Edit Form Modal */}
      <ScheduleFormModal
        visible={isFormModalVisible}
        onCancel={() => setIsFormModalVisible(false)}
        onSubmit={handleModalSubmit}
        schedule={currentSchedule ?? undefined}
        locationFilter={filters.location_id}
      />

      {/* View Schedule Modal - Using the new component */}
      <ScheduleViewModal
        visible={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        onEdit={handleEditSchedule}
        onDelete={handleDeleteSchedule}
        schedule={currentSchedule}
        isDeleting={deleteScheduleMutation.isPending}
        canModifySchedules={canModifySchedules}
        canDeleteSchedules={canDeleteSchedules}
      />
    </div>
  );
};

export default ScheduleCalendar;
